/**
 * La pantalla de bloqueo del PIN.
 *
 * ---------------------------------------------------------------------------
 *  Lo que se dice en la propia pantalla
 * ---------------------------------------------------------------------------
 *
 * Se dice, sin adornos, **qué protege y qué no**: «esto evita que quien coja tu
 * teléfono vea tus cifras; no sustituye a tu contraseña».
 *
 * Poner un candado y llamarlo «seguridad» sin más sería mentir. Y esa mentira se paga
 * el día que alguien confía en ella de verdad: deja el teléfono con la aplicación
 * abierta creyendo que el PIN la protege de algo más de lo que la protege.
 *
 * ---------------------------------------------------------------------------
 *  El botón de «olvidé el PIN»
 * ---------------------------------------------------------------------------
 *
 * Está siempre visible y no esconde nada. Un bloqueo del que no se puede salir es
 * peor que no tener bloqueo: deja a alguien fuera de sus propias cuentas por no
 * acordarse de cuatro dígitos. Al pulsarlo se cierra la sesión y se entra con la
 * contraseña, que es la que de verdad manda.
 */

import { Delete, LockKeyhole, ShieldQuestion } from 'lucide-react';
import { useCallback, useState } from 'react';

import { PIN_MAX_LENGTH, PIN_MIN_LENGTH, checkPin, markUnlocked } from '../lib/quicklock';
import { useSession } from '../lib/session';

export function PinLockScreen({ onUnlocked }: { onUnlocked: () => void }): React.JSX.Element {
  const signOut = useSession((s) => s.signOut);
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [attempts, setAttempts] = useState(0);

  const submit = useCallback(
    async (candidate: string) => {
      if (candidate.length < PIN_MIN_LENGTH) return;

      setChecking(true);
      const ok = await checkPin(candidate);
      setChecking(false);

      if (ok) {
        markUnlocked();
        onUnlocked();
        return;
      }

      // Se borra lo escrito y se cuenta el intento. El contador sirve para que quien
      // esté probando vea que se está notando, aunque no bloquee nada: esto es local
      // y cualquiera con acceso al navegador podría saltárselo de todas formas.
      setPin('');
      setAttempts((n) => n + 1);
      setError('Ese no es. Inténtalo otra vez.');
    },
    [onUnlocked],
  );

  const press = (digit: string): void => {
    setError(null);
    const next = (pin + digit).slice(0, PIN_MAX_LENGTH);
    setPin(next);

    // Al llegar al máximo se comprueba solo: en un PIN de 4 dígitos, obligar a pulsar
    // «comprobar» es un paso de más que no aporta nada.
    if (next.length === PIN_MAX_LENGTH) void submit(next);
  };

  const dots = Array.from({ length: Math.max(PIN_MIN_LENGTH, pin.length) });

  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center px-6 py-10">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-ed-gold/15">
        <LockKeyhole size={28} className="text-ed-gold" aria-hidden="true" />
      </div>

      <h1 className="mt-5 font-display text-xl font-bold">ElDescuadre está bloqueado</h1>
      <p className="mt-1 max-w-xs text-center text-sm text-ed-text-muted">
        Escribe tu PIN para ver tus cifras.
      </p>

      {/* Los puntos. Se ve cuántos se han escrito, no cuáles. */}
      <div
        className="mt-6 flex gap-3"
        role="status"
        aria-label={`${pin.length} de ${PIN_MIN_LENGTH} dígitos escritos`}
      >
        {dots.map((_, index) => (
          <span
            key={index}
            className={`h-3.5 w-3.5 rounded-full border transition-colors ${
              index < pin.length
                ? 'border-ed-gold bg-ed-gold'
                : 'border-ed-bg-border bg-transparent'
            }`}
            aria-hidden="true"
          />
        ))}
      </div>

      {error && (
        <p role="alert" className="mt-4 text-sm text-ed-coral-light">
          {error}
          {attempts >= 3 && ' Si no lo recuerdas, usa el botón de abajo.'}
        </p>
      )}

      {checking && <p className="mt-4 text-sm text-ed-text-muted">Comprobando…</p>}

      {/* El teclado numérico. Grandes y separados: se usa de pie, con una mano y con
          prisa, y un teclado pequeño se falla. */}
      <div className="mt-8 grid w-full max-w-[16rem] grid-cols-3 gap-3">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
          <button
            key={digit}
            type="button"
            className="ed-btn-secondary ed-money !min-h-[56px] text-xl"
            onClick={() => press(digit)}
            disabled={checking}
          >
            {digit}
          </button>
        ))}

        <button
          type="button"
          className="ed-btn-ghost !min-h-[56px] text-xs"
          onClick={() => setPin('')}
          disabled={checking || pin.length === 0}
        >
          Borrar
        </button>

        <button
          type="button"
          className="ed-btn-secondary ed-money !min-h-[56px] text-xl"
          onClick={() => press('0')}
          disabled={checking}
        >
          0
        </button>

        <button
          type="button"
          className="ed-btn-ghost !min-h-[56px]"
          aria-label="Borrar el último dígito"
          onClick={() => setPin((current) => current.slice(0, -1))}
          disabled={checking || pin.length === 0}
        >
          <Delete size={20} aria-hidden="true" />
        </button>
      </div>

      {pin.length >= PIN_MIN_LENGTH && (
        <button
          type="button"
          className="ed-btn-primary mt-4 w-full max-w-[16rem]"
          onClick={() => void submit(pin)}
          disabled={checking}
        >
          Comprobar
        </button>
      )}

      {/* La salida, siempre a la vista. */}
      <button
        type="button"
        className="mt-8 flex items-center gap-2 text-xs text-ed-text-muted hover:text-ed-text-white"
        onClick={() => {
          void signOut();
        }}
      >
        <ShieldQuestion size={14} aria-hidden="true" />
        Olvidé el PIN: entrar con mi contraseña
      </button>

      <p className="mt-6 max-w-xs text-center text-[11px] leading-relaxed text-ed-text-muted">
        Esto evita que quien coja tu teléfono vea tus cifras de un vistazo. No sustituye a
        tu contraseña: tus datos siguen en tu servidor y se protegen con ella.
      </p>
    </div>
  );
}
